import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * StatusChip — the prototype's `.chip` with its tone classes (T-0186).
 *
 * Mirrors `.chip`, `.chip.sm` and `.chip-{neutral|amber|healthy|at-risk|
 * critical|unassessed|frozen|ai}` in `docs/requirement-library/prototype/
 * tokens.css` and `docs/discovery/prototype/discovery.css`. Used for list
 * and record states ("Scheduled", "3 to review", "Retired", "Possible
 * duplicate"). Posture has its own chip (`PostureChip`) and AI confidence
 * its own (`AiConfidenceChip`); this one is for plain states.
 */

export type StatusChipTone =
  | 'neutral'
  | 'amber'
  | 'healthy'
  | 'at-risk'
  | 'critical'
  | 'unassessed'
  | 'frozen'
  | 'ai'
  | 'unset';

const TONE: Record<StatusChipTone, string> = {
  neutral: 'border border-ink-100 bg-paper-100 text-ink-700',
  amber: 'bg-accent-amber-100 text-accent-amber-700',
  healthy: 'bg-posture-healthy-tint text-posture-healthy',
  'at-risk': 'bg-posture-at-risk-tint text-posture-at-risk',
  critical: 'bg-posture-critical-tint text-posture-critical',
  unassessed: 'bg-ink-100 text-posture-unassessed',
  frozen: 'bg-ink-100 text-ink-300',
  ai: 'border border-ai-surface-border bg-ai-surface text-ai-ink',
  /** Nothing chosen yet (`.chip-unset`), for example "No severity" (T-0188). */
  unset: 'border border-dashed border-ink-300 bg-transparent text-ink-500',
};

export interface StatusChipProps {
  tone: StatusChipTone;
  size?: 'sm' | 'md';
  children: React.ReactNode;
  title?: string;
  className?: string;
}

export function StatusChip({ tone, size = 'md', children, title, className }: StatusChipProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full font-medium',
        size === 'sm'
          ? 'h-5 px-2 text-[11px] leading-4'
          : 'h-[22px] px-2.5 text-meta leading-[18px]',
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
